/**
 * Czech (`cs`) string catalogue — mirrors the key shape of `./en.ts`. Brand
 * names like `OVERVOLT` and weapon brand names (`Sniper Rail`, `Arc Coil`)
 * stay in English where they read as proper nouns; descriptive role copy and
 * UI verbs translate to natural, idiomatic Czech.
 *
 * `{var}` placeholders match the English keys 1:1 — the i18n loader is
 * placeholder-agnostic, so changing the wording around `{var}` is fine, but
 * never drop or rename a placeholder.
 */

import type { Catalogue } from './en';

export const cs: Catalogue = {
  meta: {
    name: 'Čeština',
    code: 'CS',
  },

  towers: {
    pulse: {
      label: 'Pulse Turret',
      short: 'PULS',
      role: 'Neutrální generalista. Spolehlivé poškození kdekoli na cestě.',
    },
    arc: {
      label: 'Arc Coil',
      short: 'ARC',
      role: 'Napojený na síť. Silnější při přebytku Pylonů, slabší při výpadku.',
    },
    cryo: {
      label: 'Cryo Emitter',
      short: 'CRYO',
      role: 'Maluje úzké hrdlo. Malý dosah — zpomalí a označí každého nepřítele v jednom místě.',
    },
    mortar: {
      label: 'Splash Mortar',
      short: 'MOŽDÍŘ',
      role: 'Nepřímá palba. Postavte ho DÁL od cesty — zblízka má mrtvou zónu.',
    },
    sniper: {
      label: 'Sniper Rail',
      short: 'SNIPER',
      role: 'Průstřel v přímce. Silný na dlouhých rovinkách; v rozích slabý.',
    },
    pylon: {
      label: 'Pylon',
      short: 'PYLON',
      role: 'Zdroj energie. Postavte jich dost na pokrytí spotřeby věží (a přebytek pro Arc).',
    },
    wall: {
      label: 'Wall Node',
      short: 'ZEĎ',
      role: 'Levný blokátor — jen v režimu Maze. Tvaruje cestu, kterou nepřátelé musí projít.',
    },
  },

  roleHints: {
    pulse: 'Pulse Turret: spolehlivá volba — solidní poškození kdekoli na cestě.',
    arc: 'Arc Coil: roste s přebytkem energie. Postavte víc Pylonů pro silnější řetěz.',
    cryo: 'Cryo Emitter: maluje úzké hrdlo. Zpomalí a označí pár dlaždic v sevření.',
    mortar: 'Splash Mortar: nepřímá palba. Postavte ho DÁL od cesty — zblízka netrefí.',
    sniper: 'Sniper Rail: průstřel v přímce. Zaměřte dlouhou rovinku pro maximální účinek.',
    pylon: 'Power Pylon: krmí síť. Postavte dost zdrojů na pokrytí spotřeby věží.',
    wall: 'Zeď: levný blokátor — tvaruje cestu, kterou nepřátelé musí projít.',
  },

  hints: {
    build:
      'Postavte věž: vyberte jednu níže (nebo stiskněte 1) a klikněte na svítící dlaždici u cesty.',
    placed: 'Dobře. Přidejte další a stiskněte mezerník pro spuštění vlny.',
    wave1: 'Věže střílí samy. Hlídejte, ať ukazatel napájení nezalétne do červené.',
    select: 'Klikněte na postavenou věž a vyberte ji — U vylepšit, S prodat.',
    brownout: 'Výpadek! Věže zpomalily. Postavte Pylon (3) a přidejte napájení.',
  },

  contextHints: {
    overload:
      'Overload: vytáhněte další vlnu pro větší odměny — za vyšší riziko. Pomalý start? Vytočte ji pro rychlejší Flux.',
    profileKey: 'Stiskněte P pro Profil — Flux odemyká nové věže, schopnosti a režimy.',
    endlessMod: 'Endless rotuje MODIFIKÁTORY vln — přizpůsobte sestavu.',
    puzzleHover:
      'Režim Puzzle: ručně tvořené logické úrovně se zdmi blokujícími výhled. Úplně jiná hra.',
  },

  hud: {
    buildZone: 'STAVBA',
    waveZone: 'VLNA',
    abilitiesZone: 'SCHOPNOSTI',
    overloadZone: 'OVERLOAD',

    startWave: 'SPUSTIT VLNU',
    nextWave: 'DALŠÍ VLNA',
    pause: 'PAUZA',
    resume: 'POKRAČOVAT',
    restart: 'RESTART',
    deployHint: 'spustit [SPACE]',
    holdHint: 'pauza [SPACE]',
    rebootHint: 'reset [R]',
    nextWaveHint: 'DALŠÍ VLNA',

    audioOn: 'ZVUK ZAP',
    audioOff: 'ZVUK VYP',

    credits: 'KREDITY',
    core: 'JÁDRO',
    wave: 'VLNA',
    endless: 'ENDLESS',
    score: 'SKÓRE',
    powerGrid: 'ENERGETICKÁ SÍŤ',

    runMode: 'REŽIM',
    difficulty: 'OBTÍŽNOST',
    profile: 'PROFIL',
    map: 'MAPA',
    clickHint: '▷ klik',

    locked: 'ZAMČENO',
    lockedShort: 'ZAMČENO',
    lockedTooltip:
      'ZAMČENO — odemkněte za ✦{cost} Flux na obrazovce Profil (stiskněte P).',
    lockedTowerNotice: '{tower} zamčeno',
    lockedAbility: 'Schopnost zamčena',
    lockedOverload: 'Tier Overload zamčen',
    lockedMode: 'Režim {mode} zamčen',
    lockedDifficulty: 'Obtížnost Hard zamčena',

    cheatPip: '🔓 CHEAT',
    profilePip: '👤 {name}',

    overloadIdle: '— overload se nastaví od vlny 1 —',
    overloadTier: 'TIER {tier} · {name}',
    overloadActive: '◉ AKTIVNÍ {sec}s',

    maxLevel: 'MAX. ÚROVEŇ',
    upgrade: 'VYLEPŠIT L{level}',
    sell: 'PRODAT',
    target: 'CÍL: {mode}',
    cycleTarget: 'přepnout · [T]',
    sellHint: '+{refund}c · [S]',
    upgradeHint: '{cost}c · [U]',
    overclock: 'OVERCLOCK',
    overclockOn: 'OVERCLOCK ✦ ZAP',

    // Sprint 69 — render.ts build-phase hint pill (uživatel hlásil v ?lang=cs).
    buildHintIntro:
      'FÁZE STAVBY  //  vyber věž [1·2·3·4·5], postav ji a STISKNI VLNU [SPACE]',
    buildHintReinforce: 'FÁZE STAVBY  //  posil síť a STISKNI VLNU {wave} [SPACE]',

    mazeRoute: 'T R A S A   B L U D I Š T Ě',
    tiles: 'DLAŽDIC',

    abilityActive: '◉ AKTIVNÍ {sec}s',
    costSuffix: '{cost}c',
    overclockPip: 'OC ×{n}',
    powerReadout: '{demand} / {supply}W  ·  {pct}%',
    towerCostDraw: '{cost}c {power}w',
    pylonCostSupply: '{cost}c +{supply}w',
    wallCost: '{cost}c 0w',
    endlessModBadge: 'MOD · {label}',
    startWaveOverload: '⚡{n} {tierName}',
    waveOverloadPip: '⚡{n} {tierName}',
    overclockNoticeOn: '{tower} Overclock ZAP',
    overclockNoticeOff: '{tower} Overclock VYP',

    tierMult: {
      hp: 'HP',
      sp: 'RY',
      cn: 'PO',
      gd: 'ZL',
      sc: 'SK',
    },
    spawnShort: {
      bit: 'Bit',
      packet: 'Pkt',
      daemon: 'Dmn',
      fork: 'Fork',
      cipher: 'Cph',
      patch: 'Pch',
      buffer: 'Buf',
      streak: 'Stk',
      kernel: 'KERNEL',
    },
  },

  modes: {
    campaign: 'KAMPAŇ',
    endless: 'ENDLESS',
    maze: 'BLUDIŠTĚ',
    puzzle: 'PUZZLE',
  },
  modeSub: {
    campaign: '10 vln · vyhrát',
    endless: '✦ {cost}',
    maze: '✦ {cost}',
    puzzle: 'vybrat úroveň',
  },
  modeBlurb: {
    campaign: '10 vln. Boss na vlně 10. Vyhraj a postupuješ dál.',
    endless: 'Stejná mapa. Modifikátory každou vlnu. Přizpůsob se, nebo prosákne.',
    maze: 'Otevřené pole. Cestu si tvoříš zdmi. Zpomal je, nebo ubírej jejich kořist.',
    puzzle: '10 ručně skládaných úrovní. Najdi tu jednu vítěznou sestavu.',
  },
  modeCard: {
    campaign: {
      tagline: 'Nauč se síť, sraz bosse.',
      bullet1: '10 ručně laděných vln + boss',
      bullet2: 'Pevná S-trasa — stav, vlna, vylepši',
      bullet3: 'Výhra odemkne Endless na této mapě',
      length: '~8 min',
      skill: 'Trénuje pořadí stavby',
    },
    endless: {
      tagline: 'Přizpůsob se, nebo prosákne. Konec není.',
      bullet1: 'Hraje Kampaň 1–10, pak roste navždy',
      bullet2: 'Nový modifikátor každou vlnu',
      bullet3: 'Konec, když jádro padne',
      length: '~30 min',
      skill: 'Trénuje dlouhé pivoty',
    },
    maze: {
      tagline: 'Stavíš bludiště, kterým musí projít.',
      bullet1: 'Otevřené pole — bez nakreslené cesty',
      bullet2: 'Věže a levné zdi tvoří trasu',
      bullet3: 'Delší trasa = víc času v palbě',
      length: '~12 min',
      skill: 'Trénuje směrování prostoru',
    },
    puzzle: {
      tagline: 'Jedna sestava vyhraje. Najdi ji.',
      bullet1: '10 úrovní, jediné správné řešení',
      bullet2: 'Pevný rozpočet a pevná vlna',
      bullet3: 'Zdi blokující výhled + limity věží',
      length: 'po ~2 min',
      skill: 'Trénuje dedukci',
    },
  },
  difficulty: {
    easy: 'LEHKÁ',
    normal: 'NORMÁLNÍ',
    hard: 'TĚŽKÁ',
  },
  difficultySub: {
    easy: 'mírná',
    normal: 'vyvážená',
    hard: '✦ {cost}',
  },

  overlay: {
    paused: 'PAUZA',
    pauseHint: 'STISK  MEZERNÍK  PRO  POKRAČOVÁNÍ',

    missionComplete: '// MISE SPLNĚNA',
    gridSecured: 'SÍŤ ZAJIŠTĚNA',
    wavesRepelled: 'Všech {total} útočných vln odraženo.',
    continueEndless: 'POKRAČOVAT  →  ENDLESS',
    unlockEndless: 'odemkněte Endless v Profilu pro pokračování',
    restartCampaign: 'nebo stiskněte  R  pro restart kampaně',

    systemFailure: '// SELHÁNÍ SYSTÉMU',
    coreBreached: 'JÁDRO PRORAŽENO',
    endlessEnded: 'Endless běh skončil na vlně {wave}.',
    reactorOverrun: 'Reaktor přemožen ve vlně {wave} z {total}.',
    runItBack: 'STISK  R  PRO  NOVÝ  POKUS',

    finalScore: 'FINÁLNÍ SKÓRE',
    newHighScore: '★  NOVÉ NEJVYŠŠÍ SKÓRE  ★',
    newEndlessBest: '★  NOVÉ ENDLESS MAXIMUM  ★',
    highScore: 'NEJVYŠŠÍ SKÓRE',
    endlessBest: 'ENDLESS MAXIMUM',

    // Sprint 69 — top-banner countdown overlays drawn by render.ts.
    aftershock: '// DOZNĚNÍ — DAŇ SÍTI  {sec}s //',
    overcharge: '// OVERCHARGE — SÍŤ POSÍLENA  {sec}s //',
    brownout: '// VÝPADEK SÍTĚ — VĚŽE ZPOMALENY //',
  },

  profile: {
    title: 'PROFIL',
    subtitle: '// META-PROGRESE',
    closeHint: 'stiskněte  P  nebo  ESC  pro zavření',
    tabUnlocks: 'ODEMKNUTÍ',
    tabScoreboard: 'SKÓRE',
    flux: 'FLUX',
    unlockTree: 'STROM ODEMKNUTÍ',
    unlockedCount: '{owned} / {total} ODEMČENO',
    lifetime: 'CELKEM',

    branchArsenal: 'ARZENÁL',
    branchModes: 'REŽIMY',
    branchChallenge: 'VÝZVA',
    branchCosmetic: 'KOSMETIKA',

    statRunsPlayed: 'POČET BĚHŮ',
    statCampaignWins: 'VÝHRY KAMPANĚ',
    statBestScore: 'NEJVYŠŠÍ SKÓRE',
    statDeepestEndless: 'NEJHLUBŠÍ ENDLESS',
    statTotalFlux: 'CELKEM FLUX',

    personalBests: 'OSOBNÍ REKORDY',
    waveLabel: 'VLNA {wave}',

    runHistory: 'HISTORIE BĚHŮ',
    noRuns: 'Žádné běhy zatím — dokončete jeden a začněte sbírat.',
    earlierRuns: '+ {n} dřívější běh',
    earlierRunsPlural: '+ {n} dřívějších běhů',
    colRun: 'BĚH',
    colMode: 'REŽIM · OBTÍŽNOST',
    colResult: 'VÝSLEDEK',
    colOverload: 'OVL',
    colScore: 'SKÓRE',
    colDeltaBest: 'Δ NEJL.',
    colFlux: 'FLUX',
    starBest: '★ NEJLEPŠÍ',

    statusUnlocked: 'ODEMČENO',
    statusOwned: 'VLASTNĚNO',
    statusRequiresPrereq: 'Nejprve {prereq}',
    statusFluxShort: 'Chybí ✦{short} Flux',
    statusAffordable: 'Dostupné — klikněte pro odemknutí',
    statusCost: '✦{cost} pro odemknutí',
    buyAction: 'KOUPIT  ▸',
    nodeReq: 'POŽAD: {prereq}',

    profileLauncher: 'PROFIL',
    profileKeyHint: '[ P ]',
    viewScoreboard: 'ZOBRAZIT  SKÓRE',
    newPersonalBest: '★  NOVÝ OSOBNÍ REKORD  ★',
    fromYourBest: 'OD VAŠEHO MAXIMA',

    grantTower: 'ÚTOČNÁ VĚŽ',
    grantAbility: 'AKTIVNÍ SCHOPNOST',
    grantMode: 'HERNÍ REŽIM',
    grantDifficulty: 'OBTÍŽNOST',
    grantOverload: 'OVERLOAD TIER {tier}',
    grantTheme: 'TÉMA DESKY',

    // Sprint 69 — náhled uzlu odemčení (původně tvrdě v profileScreen.ts).
    nodePreview: {
      towerMortar:
        'Nepřímá palba. Postav ho DÁL od cesty — zblízka má mrtvou zónu; granáty padají na shluky a drtí brnění.',
      towerSniper:
        'Průstřel v přímce. Zaměř dlouhou rovinku — každý nepřítel v linii dostane zásah; v rozích slabý.',
      abilityAirstrike:
        'Jednorázový výbuch na cílovou dlaždici (45c). Vyčisti shluk nebo ubli silnému nepříteli.',
      abilityEmp:
        'Plošný stun (80c). Zmraz na okamžik všechny nepřátele, když je linie přemožená.',
      modeEndless:
        'Hraj za vlnou 10 — procedurální vlny pokračují, dokud Jádro nepadne. Nová honba za skóre.',
      modeMaze:
        'Otevřené pole — věže blokují dlaždice a nepřátelé hledají cestu okolo (A*). Stavíš bludiště.',
      difficultyHard:
        'Tvrdší vlny; větší násobiče zlata a skóre. Odemyká horní pásmo Flux.',
      overloadCritical:
        'Overload tier 3 — vytoč to a získej tlustší násobiče skóre / Flux v další vlně.',
      overloadMeltdown:
        'Overload tier 4 — nejtěžší pevná vlna. Špičkové násobiče; jen pro stabilní sestavu.',
      themeAmber:
        'Kosmetická paleta — teplý jantarový CRT terminál. Čistě kosmetické; bez vlivu na hru.',
      themePhosphor:
        'Kosmetická paleta — starý zelený fosfor. Čistě kosmetické; bez vlivu na hru.',
    },
  },

  help: {
    eyebrow: '/ /  R E F E R E N C E',
    title: 'PROVOZNÍ PŘÍRUČKA',
    controls: 'OVLÁDÁNÍ',
    powerGrid: 'ENERGETICKÁ SÍŤ',
    powerGridBody:
      'Zbraně berou energii; Pylony ji dodávají. Když spotřeba přeroste dodávku, síť padne do výpadku a všechny věže zpomalí — postavte Pylony, ať střílíte dál.',
    objective: 'CÍL',
    objectiveBody:
      'Vyčistěte všech 15 vln kampaně a vyhrajte; prohrajete v okamžiku, kdy životy Jádra padnou na nulu. Režim Endless běží navždy.',
    mazeMode: 'REŽIM BLUDIŠTĚ',
    mazeModeBody:
      'Třetí herní režim na otevřeném poli: věže blokují dlaždice, na kterých stojí, a nepřátelé hledají cestu okolo (A*), takže stavíte bludiště. Jádro nemůžete úplně zazdít.',
    glitches: 'GLITCHE',
    towers: 'VĚŽE',
    abilities: 'SCHOPNOSTI',
    replayIntro: '↺  ZNOVU INTRO',
    closeHint: 'Esc / H / ? nebo kliknutí kamkoli zavře okno',
  },

  intro: {
    eyebrow: '/ /  S Y S T É M O V Ý   B R I E F I N G',
    titleBig: 'OVERVOLT',
    subtitle: 'Ubraňte Jádro',
    line1:
      'Poškozené programy se valí obvodem k Jádru. Postavte věže a zničte je dřív, než prorazí.',
    line2:
      '⚡ Každá věž bere energii. Postavíte-li příliš, síť spadne — všechny věže najednou zpomalí. Stavte Pylony, ať energie teče.',
    line3:
      'Stavte v klidové fázi, pak stiskněte Spustit vlnu. Přežijte 15 vln a vyhrajete.',
    line4: 'Myš staví · 1–5 výběr · Mezerník start/pauza · U vylepšit · S prodat',
    gotIt: 'ROZUMÍM  →',
    gotItHint: 'stiskněte libovolnou klávesu pro pokračování',
  },

  puzzle: {
    label: 'PUZZLE',
    levels: {
      'p0-first-light': {
        name: 'První Světlo',
        hint: 'Postavte Pulse vedle koridoru. Stačí jakákoli dlaždice v dosahu.',
      },
      'p1-long-lance': {
        name: 'Dlouhé Kopí',
        hint: 'Jeden Sniper zarovnaný s linií prostřelí každého nepřítele.',
      },
      'p2-over-the-wall': {
        name: 'Přes Zeď',
        hint: 'Zeď blokuje každý přímý paprsek. Granáty z Možďíře proletí obloukem.',
      },
      'p3-funhouse-mirror': {
        name: 'Křivá Zrcadla',
        hint: 'Sniper provléknutý mezerou zasáhne obě linie.',
      },
      'p4-surplus-riches': {
        name: 'Bohatství Navíc',
        hint: 'Postavte Pylony přes volné pásmo. Arc Coil žije z přebytku.',
      },
      'p5-pinch-point': {
        name: 'Sevření',
        hint: 'Cryo namaluje sevření; Pulsy okolo doddělají hromadu.',
      },
      'p6-cipher-gauntlet': {
        name: 'Cipher Rukavice',
        hint: 'Možďíř drtí brnění; Cryo maluje ohyb; Sniper prostřelí spodní rameno. Použijte celou sestavu.',
      },
      'p7-kernel-echo': {
        name: 'Ozvěna Kernelu',
        hint: 'Možďíře lobují přes zdi výhledu. Sniper provleče zdola. Hlídejte Power Drain.',
      },
      'p8-overclock-crunch': {
        name: 'Overclock Tíseň',
        hint: 'Jeden Pulse to nestihne. Overclockujte v rozhodující chvíli — průměrná rychlost nestačí.',
      },
      'p9-pylon-pinch': {
        name: 'Pylonové Sevření',
        hint: 'Sevřete Pylony mezi věže — aura odměňuje těsnou stavbu.',
      },
      // Sprint 74b — tvrdá úroveň (puzzle-rework-s74b).
      'p10-slit-lance': {
        name: 'Štěrbinové Kopí',
        hint: 'Jedna štěrbina. Vyberte správnou věž — druhé místo neexistuje.',
      },
      'p11-fortress': {
        name: 'Pevnost',
        hint: 'Dva Možďíře, dvě políčka. L zatáčka přesahuje jediný mezikruh.',
      },
      'p12-brittle-math': {
        name: 'Křehká Matematika',
        hint: 'Pulse poškození zaokrouhluje pod Cipher brnění. Cryo křehkost je násobitel, který zavře rovnici.',
      },
      'p14-painters-cell': {
        name: 'Malířova Cela',
        hint: 'Rozpočet uzamkne Pulse na L1. Cryo křehkost je násobitel, který zavře rovnici.',
      },
      'p15-two-mirrors': {
        name: 'Dvě Zrcadla',
        hint: 'Jedno pivotní políčko prochází mezerou — kopí zasáhne obě ramena.',
      },
      'p16-mortar-annulus': {
        name: 'Možďířový Mezikruží',
        hint: 'U je širší než koblih. Dva Možďíře — po jednom na rameno.',
      },
      'p17-no-cryo-streaks': {
        name: 'Streaky Bez Cryo',
        hint: 'Rozpočet uzamkne obě na L1. Možďíř vzadu, Sniper vepředu — bez zkratek.',
      },
    },
    // Sprint 63 (puzzle-ux-briefing-s63) — briefing card + Win / Lose
    // overlay UI strings. Per-level briefing copy stays in
    // `src/game/puzzles.ts`; only the chrome is translated.
    briefing: {
      eyebrow: 'BRIEFING PUZZLE',
      startBtn: 'Spustit ▷',
      cancelBtn: 'Zrušit',
      sectionConstraints: 'OMEZENÍ',
      sectionTowers: 'VĚŽE',
      sectionWinCondition: 'PODMÍNKA VÍTĚZSTVÍ',
      budgetLabel: 'Rozpočet zlata',
      wavesLabel: 'Vlny',
      towerAllowed: 'POVOLENO',
      towerForbidden: 'ZAKÁZÁNO',
      resultEyebrowWon: 'PUZZLE VYŘEŠENO',
      resultEyebrowLost: 'PUZZLE NEDOKONČENO',
      resultTitleWon: 'VYŘEŠENO!',
      resultTitleLost: 'ZKUSIT ZNOVU',
      resultStatTowers: 'VĚŽE',
      resultStatGold: 'UTRACENÉ ZLATO',
      resultStatLives: 'ZBÝVÁ ŽIVOTŮ',
      resultNextBtn: 'Další úroveň ▷',
      resultReplayBtn: 'Přehrát znovu',
      resultResetBtn: 'Restartovat pokus',
      resultBackBtn: 'Zpět na výběr',
    },
    // Sprint 69 — překlady chrome výběru puzzle úrovní.
    select: {
      subtitle: 'Ručně tvořené logické úrovně — odvoďte tu jednu vítěznou sestavu.',
      close: 'Zavřít',
      cardTitle: 'P{index}  {name}',
      lockedCard: '🔒 ZAMČENO',
      solved: '✓ Vyřešeno',
      unsolved: '▷ Nevyřešeno',
      noSolve: 'Zatím nevyřešeno',
      bestLine: 'Nejlepší: {towers}v · {gold}z · {power}p',
      allowed: 'Povoleno: {list}',
      parPlaceholder: 'par: — / — / —',
      parRow: '{towers}v · {gold}z · {power}p',
    },
  },

  localeChip: {
    label: 'JAZYK',
    en: 'EN',
    cs: 'CS',
  },

  resume: {
    title: 'Pokračovat v běhu?',
    resume: 'Pokračovat',
    newRun: 'Nový běh',
    hint: '[Enter] Pokračovat   ·   [Esc] Nový',
    detail:
      '{mode} · {difficulty} · vlna {wave} · {towers} věží · {gold}z · {lives}♥',
  },

  // -------- Sprint 69: chrome UI Kernel bosse --------------------------------
  boss: {
    kernelHeader: '⚠  THE KERNEL',
    phase: 'FÁZE {phase} · {name}',
    powerDrain: '⚡ ODČERPÁNÍ',
    kernelIncoming: '⚠  KERNEL  PŘICHÁZÍ  ⚠',
    phaseName: {
      boot: 'BOOT SEQUENCE',
      fork: 'FORK BOMB',
      panic: 'KERNEL PANIC',
    },
  },

  // -------- Sprint 69: mapy --------------------------------------------------
  maps: {
    default: { label: 'Datawash', tagline: 'Otevřený S-ohyb — klasický OVERVOLT layout.' },
    cramped: { label: 'Switchyard', tagline: 'Stísněné koridory — každá dlaždice se počítá.' },
    lines: { label: 'Long Lines', tagline: 'Dlouhé rovinky — provleč ten průstřel.' },
    serpentine: { label: 'Snake Run', tagline: 'Úzký had — Cryo painter září.' },
  },

  // -------- Sprint 69: schopnosti --------------------------------------------
  abilities: {
    overcharge: { label: 'Overcharge' },
    airstrike: { label: 'Airstrike' },
    empBurst: { label: 'EMP Burst' },
  },

  // -------- Sprint 69: názvy Overload tierů ----------------------------------
  overload: {
    tierName: {
      nominal: 'Nominal',
      charged: 'Charged',
      surging: 'Surging',
      critical: 'Critical',
      meltdown: 'Meltdown',
    },
  },

  // -------- Sprint 69: Endless modifikátory ----------------------------------
  endlessMod: {
    armoredSurge: { label: 'Obrněný nápor' },
    fastPack: { label: 'Rychlá smečka' },
    scatteredSpread: { label: 'Rozptyl' },
    streakRunners: { label: 'Streak běžci' },
    daemonRush: { label: 'Daemoní útok' },
  },

  // -------- Sprint 69: nepřátelé (Help roster) -------------------------------
  enemies: {
    bit: { label: 'Bit' },
    packet: { label: 'Packet' },
    daemon: { label: 'Daemon' },
    fork: { label: 'Fork' },
    cipher: { label: 'Cipher' },
    patch: { label: 'Patch' },
    buffer: { label: 'Buffer' },
    streak: { label: 'Streak' },
    kernel: { label: 'Kernel' },
  },

  // -------- Sprint 69: jednořádkové role nepřátel ----------------------------
  enemyRole: {
    bit: 'Základní bíčka — laciný, pomalý, křehký.',
    packet: 'Rychlý a slabý; řítí se k Jádru.',
    daemon: 'Těžká HP houba; po průchodu silně bere.',
    fork: 'Při zničení se rozdělí na dva Bity.',
    cipher: 'Obrněný — strhej brnění Možďířem.',
    patch: 'Léčí okolní Glitche; sundej ho jako první.',
    buffer: 'Regenerativní štít; prolom ho burst poškozením.',
    streak: 'Sám sprintuje; odolává slow. Pokryj cestu — Sniper nebo Možďíř.',
    kernel: 'Fázový boss — odčerpává energii. Sundej ho před další fází.',
  },

  // -------- Sprint 69: jednořádkové role schopností --------------------------
  abilityRole: {
    overcharge:
      'Posil síť: každá věž střílí rychleji po krátkou dobu. Naplánuj kolem těžké vlny.',
    airstrike: 'Jednorázový výbuch na cílovou dlaždici. Vyčisti shluk nebo ubli silnému nepříteli.',
    empBurst: 'Plošný stun — všichni nepřátelé na chvíli zamrznou. Kup si čas, když je linie přemožena.',
  },

  // -------- Sprint 69: chrome tooltipů věží / schopností ---------------------
  tooltip: {
    levelOf: 'LV {level}/{max}',
    credits: '{cost} kreditů',
    next: 'DALŠÍ  →  LV {level}   {cost} kreditů',
    cooldown: 'COOLDOWN  {sec}s',
    stat: {
      supply: 'DODÁVKA  +{n}w do sítě',
      dmgRngCd: 'DMG {damage}    RNG {range}    CD {cd}s',
      pwrChain: 'PWR {power}w    ŘETĚZ {chain}',
      pwrAoe: 'PWR {power}w    AOE {targets} cíle',
      slow: 'SLOW ×{factor}  na  {sec}s',
      brittle: 'KŘEHKOST +{pct}% dmg od ostatních věží',
      pwrBlast: 'PWR {power}w    BLAST {tiles} dlaždic',
      pwrSingle: 'PWR {power}w    1 cíl',
    },
  },

  // -------- Sprint 69: popisky kláves v Help panelu --------------------------
  controls: {
    arm: 'Vyber Pulse / Arc / Pylon / Cryo / Možďíř / Sniper',
    mouse: 'Postav na dlaždici · klikni na věž pro výběr',
    space: 'Spustit vlnu · pauza vlny',
    fast: 'Zrychlení (1× ⇄ 2×)',
    upgradeSell: 'Vylepšit / prodat vybranou věž',
    target: 'Přepnout cílový režim vybrané věže',
    profile: 'Otevřít Profil (strom odemčení · skóre)',
    utility: 'Zrušit · ztlumit · restart běhu',
  },

  // -------- Sprint 69: názvy uzlů odemčení ----------------------------------
  unlocks: {
    towerMortar: { name: 'Splash Mortar' },
    towerSniper: { name: 'Sniper Rail' },
    abilityAirstrike: { name: 'Airstrike' },
    abilityEmp: { name: 'EMP Burst' },
    modeEndless: { name: 'Režim Endless' },
    modeMaze: { name: 'Režim Bludiště' },
    difficultyHard: { name: 'Obtížnost Hard' },
    overloadCritical: { name: 'Overload: Critical' },
    overloadMeltdown: { name: 'Overload: Meltdown' },
    themeAmber: { name: 'Amber Terminál' },
    themePhosphor: { name: 'Zelený fosfor' },
  },

  // Sprint 62 — Auth start-screen (docs/AUTH_DESIGN.md §3.2).
  auth: {
    title: 'OVERVOLT',
    subtitle:
      'Přihlaste se a synchronizujte postup mezi zařízeními — nebo hrajte jako host, stejně jako dosud. O svůj běh nepřijdete.',
    signInDiscord: '⚡  Přihlásit přes Discord',
    signInGoogle: 'G   Přihlásit přes Google',
    signInEmail: '✉   Přihlásit přes e-mail',
    playAsGuest: '→   Hrát jako host',
    comingSoon:
      'Brzy dostupné — backend se právě staví. Přihlášení bude k dispozici, jakmile vyjde další nasazení.',
    guestMode: 'Hraješ jako host — postup se ukládá jen na tomto zařízení.',
    orDivider: 'nebo',
    signOut: 'Odhlásit',
    signOutBody:
      'Odhlásit — uložení v cloudu zůstane v bezpečí. Pro synchronizaci nových běhů se budeš muset znovu přihlásit.',
  },

  // Sprint 62 — Cookies banner (docs/AUTH_DESIGN.md §5).
  cookies: {
    title: 'Pamatujeme si tě díky cookies',
    body: 'OVERVOLT používá pár nezbytných cookies, aby ses udržel/a přihlášen/a mezi návštěvami. Žádné sledování, žádné reklamy, žádné třetí strany (kromě tvého zvoleného poskytovatele přihlášení).',
    accept: 'Přijmout',
    essentials: 'Jen nezbytné',
  },

  // Sprint 73 — interaktivní tutoriál (docs/TUTORIAL_DESIGN.md §6).
  tutorial: {
    button: {
      start: 'SPUSTIT TUTORIÁL',
      startSub: 'Poprvé? Projděte si prohlídku →',
      replay: 'PŘEHRÁT ZNOVU',
      replaySub: 'Osvěžit základy',
    },
    step: {
      welcome: {
        title: 'Vítejte v OVERVOLT',
        body: 'Jste obranná AI selhávajícího reaktoru. Porušené programy pochodují obvodem k vašemu Core — zničte je dřív, než se prolomí.',
      },
      path: {
        title: 'Cesta',
        body: 'Nepřátelé vždy jdou po této S-křivce, ze spawn dlaždice vlevo až k Core vpravo. Vy rozhodujete, kam podél ní postavíte věže.',
      },
      firstPlace: {
        title: 'Postavte první věž',
        body: 'Pulse Turret stojí 50 kreditů a tahá 4 W. Klikněte na svítící dlaždici a postavte jednu vedle cesty.',
      },
      wave1: {
        title: 'Spusťte první vlnu',
        body: 'Stiskněte SPUSTIT VLNU. Vaše Pulse pálí automaticky na cokoli v dosahu.',
      },
      towerRoles: {
        title: 'Pět věží, pět rolí',
        body: 'Pulse: spolehlivá kdekoli. Sniper: dlouhé rovinky. Možídř: nepřímá palba, postavte DÁL od cesty. Arc: roste s přebytkem energie. Cryo: maluje jedno hrdlo zpomalením + křehkostí.',
      },
      powerGrid: {
        title: 'Energetická síť',
        body: 'Každá věž tahá energii. Když poptávka přeroste nabídku, síť padá do výpadku a každá věž zpomalí. Stavte Pylony, aby svítilo — sousední Pylony buffují rychlost palby.',
      },
      overloadDial: {
        title: 'Overload — riziko vs odměna',
        body: 'Vytočte kotouč nahoru pro další vlnu: tvrdší nepřátelé, větší Flux. Stáhněte ho dolů pro klid.',
      },
      overclock: {
        title: 'Overclock věže',
        body: 'Zapněte OVERCLOCK na vybrané věži pro burst okno: rychlejší palba, vyšší odběr. Burstová výhra, dlouhodobá ztráta.',
      },
      abilities: {
        title: 'Aktivní schopnosti',
        body: 'Tři sloty: Overcharge nakopne celou síť. Airstrike shodí trvalou zónu spálení. EMP Burst omráčí každého na poli. Plánujte je — nemačkejte panicky.',
      },
      mazePreview: {
        title: 'Maze mód',
        body: 'Žádná pevná cesta — věže blokují dlaždice a nepřátelé A*-routují kolem nich. Vy stavíte cestu, kterou musí ujít.',
      },
      puzzlePreview: {
        title: 'Puzzle mód',
        body: 'Ručně udělané logické levely se zdmi linií pohledu. Pevný rozpočet, pevná vlna — najděte jediné vítězné rozložení. Jiná hra.',
      },
      wrapUp: {
        title: 'Jste připraveni',
        body: 'Běhy získávají FLUX — utraťte ho v Profilu pro odemčení nových věží, schopností a režimů. Začněte s Kampaní; vraťte se pro Endless, Maze a Puzzle.',
      },
    },
    ui: {
      eyebrow: 'KROK {n} / {total}',
      next: 'DÁLE →',
      back: '← ZPĚT',
      skip: 'PŘESKOČIT',
      play: 'HRÁT  ▸',
      skipConfirm: 'Přeskočit prohlídku? Můžete ji kdykoli přehrát z titulky.',
    },
  },
};
